/**
 * After Send: what happens next with this customer — through n8n "[C] App 8 - Calls" (REACT_APP_CALLS_URL).
 *
 *   book         Deal → Closed Won, amount = the package they booked, Note
 *   office_task  Zoho Task "Office Task" on the Deal (due date + what the office needs to do), Note
 *   follow_up    Zoho Task "Follow-up call" on the Deal (due date, no time), Note
 *
 * The Deal itself is created by the quote send (App 6), which is queued just before this — so this goes
 * through the same queue, after it. If the Deal isn't in Zoho yet, n8n answers 503 and the queue retries.
 */
import { getAccessToken } from './auth';
import { LEADS_SOURCE } from './leads';
import { queueChange, registerZohoAction } from './sync';

export const CALLS_URL = process.env.REACT_APP_CALLS_URL;
export const OUTCOMES_LIVE = LEADS_SOURCE === 'zoho' && !!CALLS_URL;

const pad = (n) => String(n).padStart(2, '0');
export const localDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const inDays = (days, from = new Date()) => {
  const d = new Date(from);
  d.setDate(d.getDate() + days);
  return localDate(d);
};
export const inMonths = (months, from = new Date()) => {
  const d = new Date(from);
  d.setMonth(d.getMonth() + months);
  return localDate(d);
};

// the quick picks on Follow up
export const FOLLOW_UP_PRESETS = [
  { id: '3d', label: '3 days', date: () => inDays(3) },
  { id: '1w', label: '1 week', date: () => inDays(7) },
  { id: '1m', label: '1 month', date: () => inMonths(1) },
];

const push = async (zoho) => {
  const token = await getAccessToken();
  if (!token) {
    const err = new Error('Sign in again to save this to Zoho.');
    err.offline = true;
    throw err;
  }
  let res;
  try {
    res = await fetch(CALLS_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: zoho.outcome, inspectionId: zoho.inspectionId, note: zoho.note, dueDate: zoho.dueDate, package: zoho.package }),
    });
  } catch (e) {
    const err = new Error('No connection — it will go to Zoho when you’re back online.');
    err.offline = true;
    throw err;
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) {
    const err = new Error(out.error || `Zoho didn’t save it (${res.status}).`);
    err.offline = res.status >= 500; // Deal not there yet / Zoho hiccup → retry · 4xx → final
    throw err;
  }
  return { zohoId: out.dealId || null };
};

registerZohoAction('deal_outcome', push);

const LABEL = { book: 'job booked', office_task: 'office task', follow_up: 'follow-up call' };

/**
 * outcome: 'book' | 'office_task' | 'follow_up'
 * book: package { name, total } · office_task: note (required) + dueDate · follow_up: dueDate (+ note)
 */
export const saveOutcome = ({ inspectionId, customerName, outcome, note = '', dueDate = '', pkg = null }) => {
  if (outcome === 'book' && !pkg) throw new Error('Pick the package they booked');
  if (outcome !== 'book' && !dueDate) throw new Error('Pick a date');
  if (outcome === 'office_task' && !note.trim()) throw new Error('Write what the office needs to do');
  const detail = outcome === 'book' ? `${pkg.name}` : `due ${dueDate}`;
  queueChange({
    type: `deal.${outcome}`,
    leadId: inspectionId || null,
    label: `${customerName || 'Customer'} — ${LABEL[outcome]} (${detail})`,
    payload: { outcome, note: note.trim(), dueDate, package: pkg },
    zoho: OUTCOMES_LIVE && inspectionId
      ? { action: 'deal_outcome', outcome, inspectionId, note: note.trim(), dueDate, package: pkg }
      : null,
  });
};
